import { HttpErrorResponse } from '@angular/common/http';
import { userIsSuperadminFromSession } from '@nebula/shared-vault/data-access';

export interface ModuleAccessErrorOptions {
  moduleLabel: string;
  fallback: string;
}

export interface ModuleInitialLoadErrorState {
  accessDenied: boolean;
  title: string;
  message: string;
}

const PERMISSION_HINT =
  /permiso|permission|acceso denegado|access denied|no autorizado|not authorized|forbidden/i;

function extractErrorDetail(err: HttpErrorResponse): string {
  const body = err.error as unknown;
  const detailRaw =
    (typeof body === 'object' && body !== null
      ? ((body as { detail?: unknown }).detail ??
        (body as { message?: unknown }).message ??
        (body as { error?: unknown }).error)
      : undefined) ?? undefined;

  if (typeof detailRaw === 'string' && detailRaw.trim()) return detailRaw.trim();
  if (Array.isArray(detailRaw)) {
    const normalized = detailRaw
      .map((item) => {
        if (typeof item === 'string') return item.trim();
        if (!item || typeof item !== 'object') return '';
        const rec = item as Record<string, unknown>;
        const loc = Array.isArray(rec['loc']) ? rec['loc'].join('.') : '';
        const msg = typeof rec['msg'] === 'string' ? rec['msg'] : '';
        const type = typeof rec['type'] === 'string' ? rec['type'] : '';
        const base = msg || type || '';
        if (!base) return '';
        return loc ? `${loc}: ${base}` : base;
      })
      .filter((v) => v.length > 0);
    if (normalized.length > 0) return normalized.join(' | ');
  }
  if (typeof body === 'string' && body.trim()) return body.trim();
  return '';
}

export function isHttpForbidden(err: unknown): err is HttpErrorResponse {
  return err instanceof HttpErrorResponse && err.status === 403;
}

function isPermissionRelatedDetail(detail: string): boolean {
  return PERMISSION_HINT.test(detail);
}

export function buildModuleNavigateDeniedMessage(moduleLabel: string): string {
  return `No tienes permisos para acceder a ${moduleLabel}.`;
}

export function buildModuleViewDeniedMessage(moduleLabel: string): string {
  return `No tienes permisos para ver ${moduleLabel}.`;
}

export function buildModuleActionDeniedMessage(moduleLabel: string): string {
  return `No tienes permisos para realizar esta acción en ${moduleLabel}.`;
}

export function resolveModuleInitialLoadError(
  err: unknown,
  options: ModuleAccessErrorOptions,
): ModuleInitialLoadErrorState {
  if (isHttpForbidden(err)) {
    if (userIsSuperadminFromSession()) {
      const detail = err instanceof HttpErrorResponse ? extractErrorDetail(err) : '';
      return {
        accessDenied: false,
        title: `No se pudo cargar ${options.moduleLabel}`,
        message:
          detail && !isPermissionRelatedDetail(detail)
            ? detail
            : 'El servidor rechazó la petición (403). Revisa tenant activo, token y entorno API.',
      };
    }
    return {
      accessDenied: true,
      title: 'Acceso restringido',
      message: buildModuleViewDeniedMessage(options.moduleLabel),
    };
  }

  if (err instanceof HttpErrorResponse) {
    const detail = extractErrorDetail(err);
    if (detail && !isPermissionRelatedDetail(detail)) {
      return {
        accessDenied: false,
        title: `No se pudo cargar ${options.moduleLabel}`,
        message: detail,
      };
    }
    return {
      accessDenied: false,
      title: `No se pudo cargar ${options.moduleLabel}`,
      message: err.message?.trim() || options.fallback,
    };
  }

  return {
    accessDenied: false,
    title: `No se pudo cargar ${options.moduleLabel}`,
    message: options.fallback,
  };
}

export interface VaultModuleLoadPatch {
  accessDenied: boolean;
  loadError: string | null;
  loadErrorTitle: string | null;
  error: string | null;
}

/** Aplica el parche de carga inicial a signals de un componente. */
export function applyVaultModuleInitialLoadErrorSignals(
  err: unknown,
  options: ModuleAccessErrorOptions,
  signals: {
    accessDenied: { set(value: boolean): void };
    loadError: { set(value: string | null): void };
    loadErrorTitle: { set(value: string): void };
  },
): void {
  const patch = patchVaultModuleInitialLoadError(err, options);
  signals.accessDenied.set(patch.accessDenied);
  signals.loadError.set(patch.loadError);
  signals.loadErrorTitle.set(patch.loadErrorTitle ?? `No se pudo cargar ${options.moduleLabel}`);
}

/** Parche de estado para carga inicial en stores o componentes. */
export function patchVaultModuleInitialLoadError(
  err: unknown,
  options: ModuleAccessErrorOptions,
): VaultModuleLoadPatch {
  const resolved = resolveModuleInitialLoadError(err, options);
  if (resolved.accessDenied) {
    return { accessDenied: true, loadError: null, loadErrorTitle: null, error: null };
  }
  return {
    accessDenied: false,
    loadError: resolved.message,
    loadErrorTitle: resolved.title,
    error: null,
  };
}

/** Mensajes en acciones puntuales (exportar, guardar, etc.). */
export function toModuleAccessErrorMessage(
  err: unknown,
  options: ModuleAccessErrorOptions,
): string {
  if (isHttpForbidden(err)) {
    return buildModuleActionDeniedMessage(options.moduleLabel);
  }

  if (err instanceof HttpErrorResponse) {
    const detail = extractErrorDetail(err);
    if (detail && !isPermissionRelatedDetail(detail)) return detail;
    return err.message?.trim() || options.fallback;
  }

  return options.fallback;
}
